import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import CommonModal from '../common/CommonModal';
import { IinitialState } from '../../reducers';
import { Button, Col, Dropdown, DropdownButton, Row } from 'react-bootstrap';
import {
    addOrUpdateFacilityContact as addOrUpdateFacilityContactAction,
    deleteFacilityContact as deleteFacilityContactAction,
    toggleModalEditFacilityContacts
} from '../../actions/manageInventoryActions';
import { AbstractControl, FormGenerator, Validators } from 'react-reactive-form';
import { FormUtil } from '../common/FormUtil';
import { IfacilityContact, Ioption, Iuser } from '../../models';
import { useTable, useFlexLayout } from 'react-table';
import { constants } from '../../constants/constants';
import { toastr } from 'react-redux-toastr';
import { msalFetch } from '../auth/Auth-Utils';
import API from '../../constants/apiEndpoints';
import { useTranslation, withTranslation, WithTranslation } from 'react-i18next';

interface Iprops {
    addOrUpdateFacilityContact: typeof addOrUpdateFacilityContactAction;
    deleteFacilityContact: typeof deleteFacilityContactAction;
}

const EditFacilityContactsModal = ({
    t,
    addOrUpdateFacilityContact,
    deleteFacilityContact
}: Iprops & WithTranslation) => {
    const dispatch = useDispatch();
    const { show, selectedFacility, tableData, initialEmailOptions, offline } = useSelector((state: IinitialState) => {
        const { selectedJob } = state.manageJob;
        const facility = state.facilities.facilitiesByID[selectedJob?.facilityID];
        const contactsData = state.contactsByFacility[facility?.id || ''] || [];
        const emailOptions = contactsData.map((fc: IfacilityContact) => ({ value: fc.email, label: fc.email }));

        return {
            show: state.manageInventory.showModalEditFacilityContacts,
            selectedFacility: facility,
            tableData: contactsData,
            initialEmailOptions: emailOptions,
            offline: state.offline
        };
    });

    const { i18n } = useTranslation();

    const defaultContact = {
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        title: '',
        phone: '',
        mobile: '',
        enableNotifications: false,
        smartlinkNotifications: false,
        isDeleted: false,
        contactFacilities: [],
        source: 0 /* MMG*/
    };

    const [form, setForm] = useState<AbstractControl>();
    const [emailOptions, setEmailOptions] = useState<Ioption[]>(initialEmailOptions);
    const [selectedContact, setSelectedContact] = useState<IfacilityContact | undefined>(defaultContact);
    const [onlineUserList, setOnlineUserList] = useState<IfacilityContact[]>();

    const currentTile = useMemo(() => constants.getTileByURL(window.location.pathname), []);

    const handleDelete = (facilityContact: IfacilityContact, facilityID: string) => {
        // Update local email options state to remove the deleted contact's email
        // This ensures the email dropdown doesn't show the deleted contact
        const updatedEmailOptions = emailOptions.filter(email => email.value !== facilityContact.email);
        setEmailOptions(updatedEmailOptions);

        // Dispatch Redux action to delete the contact from the backend and update Redux state
        // The contactsReducer will handle the state update immutably
        deleteFacilityContact(facilityContact, facilityID);

        // Reset the form to default state since the selected contact is being deleted
        setSelectedContact(defaultContact);
    };

    const onSelect = (eventKey: string | number, facilityContact: IfacilityContact) => {
        const eventKeyStr = String(eventKey);

        switch (eventKeyStr) {
            case '1':
                setSelectedContact(facilityContact);
                break;
            case '2': {
                setSelectedContact(facilityContact);

                const toastrConfirmOptions = {
                    onOk: () => handleDelete(facilityContact, selectedFacility?.id || ''),
                    onCancel: () => console.log('Delete Canceled'),
                    okText: t('facility:facilityContactForm.tableOkButton'),
                    cancelText: t('common:cancel')
                };
                toastr.confirm(t('common:deleteConfirm'), toastrConfirmOptions);
                break;
            }
            default:
                return;
        }
    };

    const columns = useMemo(
        (): any[] => [
            {
                Header: t('user:firstShort'),
                accessor: 'firstName',
                width: 120
            },
            {
                Header: t('user:lastShort'),
                accessor: 'lastName',
                width: 120
            },
            {
                Header: t('user:source'),
                accessor: 'source',
                width: 120,
                Cell: ({ value }: { value: number }) =>
                    value === 0 ? <span>MMG</span> : value === 1 ? <span>SAP</span> : <span></span>
            },
            {
                Header: t('common:actions'),
                id: 'actions',
                minWidth: 20,
                Cell: ({ row }: { row: any }) => (
                    <DropdownButton
                        drop="right"
                        variant="primary"
                        size="sm"
                        title={t('common:actions')}
                        id="contact-action-button"
                        onSelect={(eventKey: any) => onSelect(eventKey, row.original)}
                    >
                        <Dropdown.Item eventKey={1}>{t('common:edit')}</Dropdown.Item>
                        <Dropdown.Item eventKey={2} disabled={row.original.source === 1 /* SAP*/}>
                            {t('common:delete')}
                        </Dropdown.Item>
                    </DropdownButton>
                )
            }
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, i18n.language]
    );

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
        {
            columns,
            data: tableData
        },
        useFlexLayout
    );

    const handleCreateEmail = (value: string) => {
        const emailRegex = new RegExp(
            /^([\w-.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,8}|[0-9]{1,3})(\]?)$/
        );

        const email = { value, label: value };
        const foundContact = tableData.find((td: IfacilityContact) => td.email === value);

        if (foundContact) setSelectedContact(foundContact);
        else if (value.match(emailRegex)) {
            setEmailOptions(prev => [...prev, email]);
            setSelectedContact({ ...defaultContact, email: value });
        } else toastr.warning(t('toastMessage:warning'), t('toastMessage:validEmail'));
    };

    const loadEmailOptions = async (search: string): Promise<Ioption[]> => {
        if (offline.online && search?.length && search.length > 2) {
            const result = await msalFetch(API.contact.findUserOrContactByEmail, {
                method: 'get',
                params: { search }
            });
            const contacts =
                (result.data?.contacts as IfacilityContact[]).filter(
                    (contact, index, self) =>
                        self.findIndex(innerContact => innerContact.email === contact.email) === index
                ) || [];
            const usersAsContacts: IfacilityContact[] = result.data?.users
                ?.filter(
                    (user: Iuser) =>
                        !result.data?.contacts?.find((contact: IfacilityContact) => contact.userID === user.id) &&
                        !contacts.find(contact => contact.email === user.email)
                )
                .map((user: Iuser) => ({
                    ...defaultContact,
                    firstName: user.first,
                    lastName: user.last,
                    email: user.email,
                    phone: user.phone || ''
                }));

            const fullList = [...(usersAsContacts || []), ...contacts];
            setOnlineUserList(fullList);
            const returnList = fullList.map(({ email }: IfacilityContact) => ({ label: email, value: email }));
            return returnList as Ioption[];
        } else return emailOptions;
    };

    const fieldConfig = useMemo(() => {
        const sapContact = selectedContact?.source === 1; /* SAP */
        const formControls = {
            email: {
                render: FormUtil.AsyncCreatableSelect,
                meta: {
                    label: t('common:email'),
                    colWidth: 12,
                    rows: 2,
                    name: 'email',
                    required: true,
                    isMulti: false,
                    handleCreate: handleCreateEmail,
                    isClearable: true,
                    loadOptions: (value: string) => loadEmailOptions(value)
                },
                options: {
                    validators: [FormUtil.validators.requiredWithTrim]
                },
                formState: {
                    value: {
                        value: selectedContact?.email || '',
                        label: selectedContact?.email || ''
                    },
                    disabled: sapContact
                }
            },
            firstName: {
                render: FormUtil.TextInput,
                meta: {
                    label: t('common:firstName'),
                    colWidth: 6,
                    rows: 2,
                    name: 'firstName',
                    initialContent: '',
                    required: true
                },
                options: {
                    validators: [
                        Validators.maxLength(250),
                        Validators.minLength(2),
                        FormUtil.validators.requiredWithTrim
                    ]
                },
                formState: {
                    value: selectedContact?.firstName || '',
                    disabled: sapContact
                }
            },
            lastName: {
                render: FormUtil.TextInput,
                meta: {
                    label: t('common:lastName'),
                    colWidth: 6,
                    rows: 2,
                    name: 'lastName',
                    initialContent: '',
                    required: true
                },
                options: {
                    validators: [
                        Validators.maxLength(250),
                        Validators.minLength(2),
                        FormUtil.validators.requiredWithTrim
                    ]
                },
                formState: {
                    value: selectedContact?.lastName || '',
                    disabled: sapContact
                }
            },
            title: {
                render: FormUtil.TextInput,
                meta: {
                    label: t('common:title'),
                    colWidth: 12,
                    rows: 2,
                    name: 'title',
                    initialContent: '',
                    required: true
                },
                options: {
                    validators: [Validators.maxLength(250), FormUtil.validators.requiredWithTrim]
                },
                formState: {
                    value: selectedContact?.title || '',
                    disabled: sapContact
                }
            },
            phone: {
                render: FormUtil.TextInput,
                meta: {
                    label: t('common:phone'),
                    colWidth: 6,
                    rows: 2,
                    name: 'phone',
                    initialContent: '',
                    required: false
                },
                options: {
                    validators: [Validators.maxLength(22), Validators.minLength(7)]
                },
                formState: {
                    value: selectedContact?.phone || '',
                    disabled: sapContact
                }
            },
            mobile: {
                render: FormUtil.TextInput,
                meta: {
                    label: t('common:mobile'),
                    colWidth: 6,
                    rows: 2,
                    name: 'mobile',
                    initialContent: '',
                    required: false
                },
                options: {
                    validators: [Validators.maxLength(22), Validators.minLength(7)]
                },
                formState: {
                    value: selectedContact?.mobile || '',
                    disabled: sapContact
                }
            },
            enableNotifications: {
                render: FormUtil.Toggle,
                meta: {
                    label: t('manageInventory:enableNotifications'),
                    colWidth: 12,
                    rows: 2,
                    name: 'enableNotifications',
                    initialContent: false
                },
                formState: {
                    value: selectedContact?.enableNotifications || false,
                    disabled: false
                }
            },
            smartlinkNotifications: {
                render: FormUtil.Toggle,
                meta: {
                    label: t('manageInventory:smartlinkNotifications'),
                    colWidth: 12,
                    rows: 2,
                    name: 'smartlinkNotifications',
                    initialContent: false
                },
                formState: {
                    value: selectedContact?.smartlinkNotifications || false,
                    disabled: false
                }
            }
        };

        return FormUtil.translateForm({ controls: { ...formControls } }, t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailOptions, selectedContact, i18n.language]);

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!form || form.status === 'INVALID') {
            form?.markAsSubmitted();
            toastr.error(t('toastMessage:invalidFormSubmission'), '', constants.toastrError);
        } else {
            const newContactFacility = {
                facilityID: selectedFacility?.id,
                contactID: selectedContact?.id
            };
            const tableRow = tableData.find((datum: IfacilityContact) => datum.id === selectedContact?.id);
            const isAdd = !tableRow;

            const body = {
                ...form.value,
                email: form.value.email.value,
                contactFacilities: selectedContact?.contactFacilities
                    ? [...selectedContact.contactFacilities, newContactFacility]
                    : [newContactFacility],
                id: selectedContact?.id || null,
                source: tableRow?.source || defaultContact.source
            };

            addOrUpdateFacilityContact(body, selectedFacility?.id || '', isAdd);
            setSelectedContact(defaultContact);
        }
    };

    const onHide = () => dispatch(toggleModalEditFacilityContacts());

    useEffect(() => {
        const onValueChange = (values: any) => {
            const { email } = values;

            const selectedFacilityContact = offline.online
                ? onlineUserList?.find(user => user.email === email?.value)
                : tableData.find((td: IfacilityContact) => td.email === email?.value);

            if (selectedFacilityContact) setSelectedContact(selectedFacilityContact);
        };

        form?.valueChanges.unsubscribe(onValueChange);
        form?.valueChanges.subscribe(onValueChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, onlineUserList]);

    return (
        <CommonModal className="edit-facility-contacts-modal" title={t('facility:contacts')} show={show} onHide={onHide}>
            <div>
                <form onSubmit={onSubmit} className="clearfix beacon-form">
                    <Col xs={12}>
                        <Row>
                            <FormGenerator
                                onMount={(formControl: AbstractControl) => setForm(formControl)}
                                fieldConfig={fieldConfig}
                            />
                        </Row>
                    </Col>
                    <div className="edit-facility-form-buttons">
                        {selectedContact?.id && (
                            <Button
                                variant="primary"
                                type="button"
                                className="cancel-button"
                                onClick={() => setSelectedContact(defaultContact)}
                            >
                                {t('common:cancel')}
                            </Button>
                        )}
                        <Button variant="primary" type="submit" className="submit-button">
                            {t(
                                tableData.find((contact: IfacilityContact) => contact.id === selectedContact?.id)
                                    ? 'common:update'
                                    : 'common:add'
                            )}
                        </Button>
                    </div>
                </form>

                <div className="edit-facility-contacts-table">
                    <div
                        {...getTableProps()}
                        className={`ReactTable beacon-table contacts -highlight ${currentTile.color} flex-layout`}
                    >
                        <div className="rt-thead -header">
                            {headerGroups.map(headerGroup => (
                                <div
                                    {...headerGroup.getHeaderGroupProps()}
                                    key={headerGroup.getHeaderGroupProps().key}
                                    className="rt-tr"
                                >
                                    {headerGroup.headers.map(column => (
                                        <div
                                            {...column.getHeaderProps({ className: 'rt-th' })}
                                            key={column.getHeaderProps().key}
                                        >
                                            {column.render('Header')}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                        <div {...getTableBodyProps()} className="rt-tbody">
                            {rows.map((row, rowIndex) => {
                                prepareRow(row);
                                return (
                                    <div
                                        {...row.getRowProps()}
                                        key={row.getRowProps().key}
                                        className={`rt-tr ${rowIndex % 2 === 0 ? '-odd' : '-even'}`}
                                    >
                                        {row.cells.map(cell => (
                                            <div
                                                {...cell.getCellProps()}
                                                key={cell.getCellProps().key}
                                                className={`rt-td ${
                                                    cell.column.id === 'actions' ? 'actions-column' : ''
                                                }`}
                                            >
                                                {cell.render('Cell')}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="form-buttons text-right">
                    <Button variant="default" type="button" className="cancel-button" onClick={onHide}>
                        {t('common:close')}
                    </Button>
                </div>
            </div>
        </CommonModal>
    );
};

export default connect(() => ({}), {
    addOrUpdateFacilityContact: addOrUpdateFacilityContactAction,
    deleteFacilityContact: deleteFacilityContactAction
})(withTranslation()(EditFacilityContactsModal));
